import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSignIn, useAuth } from '@clerk/clerk-react';
import MarketingLayout from '../components/MarketingLayout';

/**
 * Redeems a Clerk sign-in token (ticket) from the query string.
 * Used for one-click sign-in links from health check and magic link emails.
 */
export default function AcceptSignInPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { signIn, setActive, isLoaded } = useSignIn();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const ticket = searchParams.get('ticket');

  useEffect(() => {
    if (!isLoaded || !signIn) return;

    // Already signed in
    if (isSignedIn) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (!ticket) {
      setStatus('error');
      setErrorMessage('Missing or invalid sign-in link. Please request a new one.');
      return;
    }

    let cancelled = false;

    const redeem = async (retry = false) => {
      try {
        const result = await signIn.create({
          strategy: 'ticket',
          ticket,
        });

        if (cancelled) return;

        if (result.status === 'complete' && result.createdSessionId) {
          await setActive({ session: result.createdSessionId });
          if (cancelled) return;
          setStatus('success');
          navigate('/dashboard', { replace: true });
        } else {
          setStatus('error');
          setErrorMessage(
            'Sign-in could not be completed. Please try again or sign in with your email.'
          );
        }
      } catch (err: any) {
        if (cancelled) return;
        // Retry once after a short delay (handles cold-load race when landing from email)
        if (!retry) {
          await new Promise((r) => setTimeout(r, 800));
          if (cancelled) return;
          redeem(true);
          return;
        }
        setStatus('error');
        const msg =
          err?.errors?.[0]?.message ||
          err?.message ||
          'Invalid or expired link. Please request a new sign-in link.';
        setErrorMessage(msg);
      }
    };

    // Small delay so Clerk is fully ready when landing from email (avoids redirect race)
    const t = setTimeout(() => {
      redeem();
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [ticket, signIn, setActive, isLoaded, isSignedIn, navigate]);

  return (
    <MarketingLayout>
      <section className="public-section">
        <div className="public-container">
          <div className="public-card public-card--white p-8 max-w-lg mx-auto text-center">
            {status !== 'error' && (
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-6" />
            )}
            <p className="public-eyebrow">Back to your bigger picture</p>
            <h1 className="text-3xl font-bold mb-4">
              {status === 'error'
                ? 'Let’s get you back into your plan.'
                : 'Your workspace is one moment away.'}
            </h1>
            <p className="text-gray-600 mb-6" role={status === 'error' ? 'alert' : 'status'}>
              {status === 'error'
                ? errorMessage
                : 'We’re checking your sign-in link and opening your dashboard.'}
            </p>
            {status === 'error' && (
              <a href="/sign-in" className="public-button public-button--primary">
                Sign in with your email
              </a>
            )}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
